/*
  product-form.js - Nook-It - PATCH SimGym 2026-08-24 (applique le 27/09/2026)

  Corrige le bug "ajout au panier intermittent qui renvoie vers /cart/add" :

  1. this.submitButtons (pluriel) : le bouton sticky et le bouton principal
     partagent le meme id DOM. querySelector() n'en renvoyait qu'un seul, donc
     l'etat "loading" / aria-disabled restait bloque sur l'autre bouton.
  2. Garde-fous null sur [name=id] et sur .loading-overlay__spinner : une
     exception dans le constructeur empechait l'upgrade du custom element,
     et le formulaire repartait alors en POST natif vers /cart/add.
  3. Le champ return_to (ajoute dans buy-buttons.liquid comme filet de securite
     pour le POST natif) est retire du FormData avant l'appel AJAX.
  4. Le .catch() ne se contente plus d'un console.error silencieux : il affiche
     un message a l'acheteur et reactive le bouton. C'est ce silence qui
     produisait le "panier vide" constate par SimGym.
  5. Detection des reponses non-JSON / HTTP en erreur (app tierce, rate limit)
     avant le .json(), qui levait sinon une exception avalee.
*/
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        const variantIdInput = this.form.querySelector('[name=id]');
        if (variantIdInput) variantIdInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Il peut y avoir 2 boutons submit (principal + sticky) dans le meme form.
        this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButton = this.submitButtons[0];

        if (document.querySelector('cart-drawer')) {
          this.submitButtons.forEach((button) => button.setAttribute('aria-haspopup', 'dialog'));
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get spinners() {
        return Array.from(this.querySelectorAll('.loading-overlay__spinner'));
      }

      setLoading(isLoading) {
        this.submitButtons.forEach((button) => {
          button.classList.toggle('loading', isLoading);
          if (isLoading) {
            button.setAttribute('aria-disabled', true);
          } else if (!this.error) {
            button.removeAttribute('aria-disabled');
          }
        });
        this.spinners.forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;
        this.setLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // Filet de securite pour le POST natif uniquement : inutile en AJAX.
        formData.delete('return_to');

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const text = await response.text();
            try {
              return JSON.parse(text);
            } catch (parseError) {
              // Reponse HTML (page d'erreur, app tierce, WAF) : on la traite
              // comme une erreur metier au lieu de laisser passer l'exception.
              throw new Error(`Reponse non-JSON de ${routes.cart_add_url} (HTTP ${response.status})`);
            }
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButtons.forEach((button) => {
                button.setAttribute('aria-disabled', true);
                const label = button.querySelector('span');
                if (label) label.classList.add('hidden');
                const message = button.querySelector('.sold-out-message');
                if (message) message.classList.remove('hidden');
              });
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('[product-form] Ajout au panier impossible :', e);
            this.error = false; // on reactive le bouton : l'acheteur peut reessayer
            this.handleErrorMessage(
              window.productFormStrings && window.productFormStrings.addError
                ? window.productFormStrings.addError
                : "L'ajout au panier a échoué. Merci de réessayer."
            );
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: e.message,
              message: e.message,
            });
          })
          .finally(() => {
            this.setLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
